import React, { useMemo } from 'react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@features/ui';
import { TOption } from '@src/types';
import { cn } from '@lib/utils.ts';
import { X } from 'lucide-react';

interface InputSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options?: TOption[];
  placeholder?: string;
  className?: string;
  borderColor?: string;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  allowClear?: boolean;
}

export const InputSelect: React.FC<InputSelectProps> = ({
  value,
  onChange,
  placeholder,
  options = [],
  error,
  borderColor: borderColorProps,
  className,
  disabled,
  id,
  allowClear,
}) => {
  const borderColor = useMemo(() => {
    if (disabled) {
      return 'border-muted-foreground';
    }
    if (error) {
      return 'border-red-500';
    }
    if (borderColorProps) {
      return borderColorProps;
    }

    return 'border-[#C6C6C6]';
  }, [disabled, error, borderColorProps]);

  return (
    <div className="relative group">
      <Select disabled={disabled} value={value} onValueChange={onChange}>
        <SelectTrigger
          className={cn(
            'h-9.5 rounded-[3px] text-sm',
            !value && 'text-muted-foreground',
            borderColor,
            className
          )}
          disabled={disabled}
          id={id}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
        {value && allowClear && (
          <button
            className="absolute right-7 top-1/2 -translate-y-1/2 rounded-sm p-1 group-hover:bg-[#555555] text-accent-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => {
              onChange('');
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </Select>
    </div>
  );
};
